import React, { useState } from "react";
import { FaTimes } from "react-icons/fa";

type CustomerDetails = {
  customerName?: string;
  customerEmail?: string;
  customerMobile?: string;
  customerCount?: number;
  waiterName?: string;
};

type Props = {
  open: boolean;
  tableName: string;
  onClose: () => void;
  onSubmit: (details: CustomerDetails) => Promise<void>;
  error?: string;
};

const CustomerDetailsForm: React.FC<Props> = ({ open, tableName, onClose, onSubmit, error }) => {
  const [customerName, setCustomerName] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");
  const [customerMobile, setCustomerMobile] = useState("");
  const [customerCount, setCustomerCount] = useState(1);
  const [waiterName, setWaiterName] = useState(() => window.localStorage.getItem("waiterName") || "");
  const [loading, setLoading] = useState(false);

  if (!open) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    await onSubmit({
      customerName: customerName || undefined,
      customerEmail: customerEmail || undefined,
      customerMobile: customerMobile || undefined,
      customerCount,
      waiterName: waiterName || undefined,
    });
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-2 sm:p-4 bg-black/50 dark:bg-black/70">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-sm sm:max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-3 sm:p-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-sm sm:text-lg font-bold text-gray-900 dark:text-white">
            Start Order - {tableName}
          </h2>
          <button
            onClick={onClose}
            disabled={loading}
            className="p-1 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-white disabled:opacity-50"
            aria-label="close modal"
          >
            <FaTimes size={16} className="sm:w-5 sm:h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-3 sm:p-4 md:p-6 space-y-3 sm:space-y-4">
          <div>
            <label className="block text-xs sm:text-sm font-semibold mb-1 text-gray-700 dark:text-gray-300">
              Customer Name (optional)
            </label>
            <input
              type="text"
              value={customerName}
              onChange={(e) => setCustomerName(e.target.value)}
              placeholder="Enter customer name"
              className="w-full px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg border-2 text-xs sm:text-sm transition-colors duration-200 focus:outline-none focus:ring-2 bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:border-orange-500 focus:ring-orange-500/50 dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder-gray-400 dark:focus:border-orange-500 dark:focus:ring-orange-500/50"
            />
          </div>

          <div>
            <label className="block text-xs sm:text-sm font-semibold mb-1 text-gray-700 dark:text-gray-300">
              Email (optional)
            </label>
            <input
              type="email"
              value={customerEmail}
              onChange={(e) => setCustomerEmail(e.target.value)}
              placeholder="customer@example.com"
              className="w-full px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg border-2 text-xs sm:text-sm transition-colors duration-200 focus:outline-none focus:ring-2 bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:border-orange-500 focus:ring-orange-500/50 dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder-gray-400 dark:focus:border-orange-500 dark:focus:ring-orange-500/50"
            />
          </div>

          <div>
            <label className="block text-xs sm:text-sm font-semibold mb-1 text-gray-700 dark:text-gray-300">
              Mobile (optional)
            </label>
            <input
              type="tel"
              value={customerMobile}
              onChange={(e) => setCustomerMobile(e.target.value)}
              placeholder="+91 9876543210"
              className="w-full px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg border-2 text-xs sm:text-sm transition-colors duration-200 focus:outline-none focus:ring-2 bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:border-orange-500 focus:ring-orange-500/50 dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder-gray-400 dark:focus:border-orange-500 dark:focus:ring-orange-500/50"
            />
          </div>

          <div>
            <label className="block text-xs sm:text-sm font-semibold mb-1 text-gray-700 dark:text-gray-300">
              Number of Guests
            </label>
            <input
              type="number"
              value={customerCount}
              min={1}
              max={20}
              onChange={(e) => setCustomerCount(Math.max(1, Math.min(20, Number(e.target.value))))}
              className="w-full px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg border-2 text-xs sm:text-sm font-medium transition-colors duration-200 focus:outline-none focus:ring-2 bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:border-orange-500 focus:ring-orange-500/50 dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder-gray-400 dark:focus:border-orange-500 dark:focus:ring-orange-500/50"
            />
          </div>

          <div>
            <label className="block text-xs sm:text-sm font-semibold mb-1 text-gray-700 dark:text-gray-300">
              Waiter Name (optional)
            </label>
            <input
              type="text"
              value={waiterName}
              onChange={(e) => setWaiterName(e.target.value)}
              placeholder="Enter waiter name"
              className="w-full px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg border-2 text-xs sm:text-sm transition-colors duration-200 focus:outline-none focus:ring-2 bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:border-orange-500 focus:ring-orange-500/50 dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder-gray-400 dark:focus:border-orange-500 dark:focus:ring-orange-500/50"
            />
          </div>

          <div className="flex gap-2 sm:gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="flex-1 px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg font-semibold text-xs sm:text-sm transition-colors duration-200 border border-gray-300 text-gray-900 hover:bg-gray-50 dark:border-gray-600 dark:text-white dark:hover:bg-gray-700 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg font-semibold text-xs sm:text-sm text-white bg-orange-600 hover:bg-orange-700 transition-colors duration-200 dark:bg-orange-600 dark:hover:bg-orange-700 disabled:opacity-50"
            >
              {loading ? "Saving..." : "Continue to Order"}
            </button>
          </div>
          {error && (
            <div className="mt-4 p-3 bg-red-100 border border-red-300 text-red-700 rounded">
              {error}
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default CustomerDetailsForm;